import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  Eye,
  FileText,
  User,
  Clock,
  MessageSquare,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  BookOpen,
  Shield,
} from 'lucide-react';
import { salesManagerService } from '../services/sales-manager.service';
import type { TeamMember } from '../types';

export const RecruitApprovalPanel: React.FC = () => {
  const [recruits, setRecruits] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedRecruit, setSelectedRecruit] = useState<TeamMember | null>(null);
  const [approvalNotes, setApprovalNotes] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchPendingRecruits = async () => {
    try {
      setIsLoading(true);
      setError('');
      // In a real app, we'd filter by 'pending_approval' status
      const response = await salesManagerService.getTeamRecruits({ status: 'pending' });
      setRecruits(response.recruits);
    } catch (err: any) {
      console.warn('API fetch failed, falling back to mock recruits for testing');
      setRecruits([
        {
          id: '1',
          userId: 'u1',
          firstName: 'John',
          lastName: 'Smith',
          email: 'john.smith@example.com',
          phone: '555-0123',
          role: 'AGENT',
          status: 'inactive',
          salesCount: 0,
          recruitCount: 0,
          currentFunnelStep: 3,
          stepName: 'Registered',
          joinedAt: new Date().toISOString(),
          performance: 'normal'
        },
        {
          id: '2',
          userId: 'u2',
          firstName: 'Sarah',
          lastName: 'Johnson',
          email: 'sarah.j@example.com',
          phone: '555-0124',
          role: 'AGENT',
          status: 'inactive',
          salesCount: 0,
          recruitCount: 0,
          currentFunnelStep: 5,
          stepName: 'Training Done',
          joinedAt: new Date(Date.now() - 86400000).toISOString(),
          performance: 'normal'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingRecruits();
  }, []);

  const handleAction = async (action: 'APPROVE' | 'DENY') => {
    if (!selectedRecruit) return;

    try {
      setActionLoading(true);
      await salesManagerService.approveRecruit({
        recruitId: selectedRecruit.id,
        action,
        notes: approvalNotes,
      });

      // Refresh list and clear selection
      await fetchPendingRecruits();
      setSelectedRecruit(null);
      setApprovalNotes('');
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${action.toLowerCase()} recruit`);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRecruits = recruits.filter(r => 
    `${r.firstName} ${r.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading && recruits.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 font-medium">Loading approval queue...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Recruit Approval Queue</h2>
          <p className="text-gray-600">Review and approve new recruits before they can start selling</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search recruits..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
          <button
            onClick={fetchPendingRecruits}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recruit List */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" />
                Pending Review ({filteredRecruits.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {filteredRecruits.length === 0 ? (
                <div className="p-8 text-center">
                  <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No pending recruits</p>
                </div>
              ) : (
                filteredRecruits.map((recruit) => (
                  <button
                    key={recruit.id}
                    onClick={() => setSelectedRecruit(recruit)}
                    className={`w-full p-4 text-left hover:bg-blue-50 transition-colors ${
                      selectedRecruit?.id === recruit.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold">
                        {recruit.firstName[0]}{recruit.lastName[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">
                          {recruit.firstName} {recruit.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{recruit.email}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Details Panel */}
        <div className="lg:col-span-2">
          {selectedRecruit ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-24">
              {/* Profile Header */}
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-2xl font-bold border-2 border-white/30">
                      {selectedRecruit.firstName[0]}{selectedRecruit.lastName[0]}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        {selectedRecruit.firstName} {selectedRecruit.lastName}
                      </h3>
                      <p className="text-blue-100 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Application Submitted: {new Date(selectedRecruit.joinedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
                    Step {selectedRecruit.currentFunnelStep}: {selectedRecruit.stepName}
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-8">
                {/* Application Details Sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Info */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                      <User className="w-4 h-4 text-blue-600" />
                      Candidate Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email:</span>
                        <span className="text-gray-900 font-medium">{selectedRecruit.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-900 font-medium">{selectedRecruit.phone || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Location:</span>
                        <span className="text-gray-900 font-medium">Michigan, US</span>
                      </div>
                    </div>
                  </div>

                  {/* Documents & Training */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      Compliance & Training
                    </h4>
                    <div className="space-y-3">
                      <button className="w-full flex items-center justify-between p-2 rounded border border-gray-200 hover:bg-gray-50 text-sm group">
                        <span className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-500" />
                          Tax Documents (W9)
                        </span>
                        <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                      </button>
                      <button className="w-full flex items-center justify-between p-2 rounded border border-gray-200 hover:bg-gray-50 text-sm group">
                        <span className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-blue-500" />
                          Training Completion Certificate
                        </span>
                        <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Training Results */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wider">Onboarding Performance</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Exam Score</p>
                      <p className="text-lg font-bold text-green-600">92%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Modules Done</p>
                      <p className="text-lg font-bold text-blue-600">8/8</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Profile Completion</p>
                      <p className="text-lg font-bold text-indigo-600">100%</p>
                    </div>
                  </div>
                </div>

                {/* Review Notes */}
                <div className="space-y-3">
                  <label className="block font-bold text-gray-900 text-sm flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    Review Notes (Optional)
                  </label>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add notes for the decision..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none min-h-[100px] text-sm"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-4 pt-4 border-t">
                  <button
                    onClick={() => handleAction('DENY')}
                    disabled={actionLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-red-200 text-red-600 rounded-xl font-bold hover:bg-red-50 hover:border-red-600 transition-all disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5" />
                    Deny Candidate
                  </button>
                  <button
                    onClick={() => handleAction('APPROVE')}
                    disabled={actionLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all disabled:opacity-50"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Approve Recruit
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-12 text-center h-full flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                <User className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a Recruit</h3>
              <p className="text-gray-500 max-w-sm">
                Select a candidate from the left queue to review their application, training results, and tax documents.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruitApprovalPanel;
